import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { downloadDocument } from '../lib/downloadDocument';
import { formatDate, formatDateTime } from '../lib/orgTimezone';
import { formatTagLabel } from '../lib/formatTag';
import { humanizeToken } from '../lib/inlineAttribution';
import { NewDocsBanner } from './NewDocsBanner';
import { Badge, Button, Input, Select } from '../atoms';
import { US_STATES } from '../lib/us-states';
import { StatCard, StatCardRow, DocumentViewer, Modal, useToast } from '../organisms';
import { SignaturePad } from '../organisms/SignaturePad';
import { PortalLayout } from './PortalLayout';
import { QueryState, PortalTable, SectionTitle, money, fileToBase64 } from './primitives';
import { PayInvoiceButton, PayAllButton, type PayableInvoice } from './PortalPay';
import { DEFAULT_PORTAL_DOC_CATEGORIES, filterCategories, withOtherCategory } from './docCategories';
import { RequestsAccordion } from './RequestsAccordion';
import {
  PropertyDetailModal, ParcelDetailModal, AppealDetailModal, InvoiceDetailModal,
  type PortalProperty, type PortalParcel, type PortalAppeal, type PortalInvoice,
} from './PortalDetail';

/**
 * ClientPortalApp (WS-3.2 + build/portal-v1) — the role='client' surface. Consumes
 * the /api/client-portal/* GET endpoints (all client-scoped server-side). V1 adds:
 *   • a reorganized Documents view (all docs together, grouped STATE → PROPERTY →
 *     YEAR, category filter + search + an upload affordance);
 *   • on-demand CAM Reports (run/download the firm's reports over the client's data);
 *   • a client ⇆ firm Comments thread (posting emails the firm).
 * Tab switching is local state, staying within the S07 base:'/' routing.
 */

interface PortalUser { name: string; email: string; org_name?: string | null }

type Tab = 'summary' | 'properties' | 'documents' | 'requests' | 'reports' | 'invoices' | 'appeals' | 'comments' | 'notifications' | 'signature';

interface NotifRow {
  id: string;
  trigger_type: string;
  title: string;
  body: string | null;
  entity_id: string | null;
  created_at: string;
  read: number | boolean;
}
const isUnread = (r: NotifRow) => !(r.read === true || r.read === 1);
const TABS = [
  { key: 'summary', label: 'Overview' },
  { key: 'properties', label: 'Properties' },
  { key: 'documents', label: 'Documents' },
  { key: 'requests', label: 'Requests' },
  { key: 'reports', label: 'Reports' },
  { key: 'invoices', label: 'Invoices' },
  { key: 'appeals', label: 'Appeals' },
  { key: 'comments', label: 'Comments' },
  { key: 'notifications', label: 'Notifications' },
  { key: 'signature', label: 'Signature' },
];

const dt = (s?: string | null) => formatDate(s);

export function ClientPortalApp({ user, onLogout }: { user?: PortalUser; onLogout: () => void }) {
  const qc = useQueryClient();
  const [active, setActive] = useState<Tab>('summary');

  // Unread firm replies (5.6): poll the client-directed notification feed so the
  // Comments tab surfaces a badge when the firm posts a reply in the portal thread.
  // D3 — the client-directed notification feed (poll). The Notifications tab lists
  // every notification (documents shared, appeal-stage advances, transmittals,
  // property-request updates, firm replies); the Comments tab keeps its own badge
  // scoped to firm replies only.
  const notifs = useQuery({
    queryKey: ['cp', 'notifications'],
    queryFn: ({ signal }) => api.get<{ unread_count: number; notifications: NotifRow[] }>('/api/client-portal/notifications', signal),
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
  // ATA part 3 §6: the firm's portal toggles. A documents-only portal hides Comments and Notifications.
  const settings = useQuery({
    queryKey: ['cp', 'settings'],
    queryFn: ({ signal }) => api.get<{ allow_comments: boolean; allow_notifications: boolean; esignature_enabled: boolean }>('/api/client-portal/settings', signal),
    staleTime: 5 * 60 * 1000,
  });
  const allowComments = settings.data?.allow_comments !== false;
  const allowNotifications = settings.data?.allow_notifications !== false;
  const allowSignature = settings.data?.esignature_enabled !== false;
  const allNotifs = notifs.data?.notifications ?? [];
  const unreadTotal = notifs.data?.unread_count ?? 0;
  const unreadReplies = allNotifs.filter((r) => r.trigger_type === 'firm_comment_added' && isUnread(r)).length;

  const seen = useMutation({
    mutationFn: () => api.post('/api/client-portal/notifications/seen', {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cp', 'notifications'] }),
  });

  // Entering the Notifications (or Comments) tab marks the client's notifications
  // read. Idempotent + self-limiting: once cleared the counts are 0 so it stops.
  useEffect(() => {
    const clear = (active === 'notifications' && unreadTotal > 0) || (active === 'comments' && unreadReplies > 0);
    if (clear && !seen.isPending) seen.mutate();
  }, [active, unreadTotal, unreadReplies, seen]);

  const tabs = TABS.filter((t) => (t.key !== 'comments' || allowComments) && (t.key !== 'notifications' || allowNotifications) && (t.key !== 'signature' || allowSignature)).map((t) => {
    if (t.key === 'notifications' && unreadTotal > 0) return { ...t, badge: unreadTotal };
    if (t.key === 'comments' && unreadReplies > 0) return { ...t, badge: unreadReplies };
    return t;
  });

  return (
    <PortalLayout
      brandName={user?.org_name ?? 'AppealStack'}
      portalLabel="Client portal"
      userLabel={user?.name}
      tabs={tabs}
      active={active}
      onSelect={(k) => setActive(k as Tab)}
      onLogout={onLogout}
    >
      <NewDocsBanner onOpenDocuments={() => setActive('documents')} isActive={active === 'documents'} />
      {active === 'summary' && <SummaryPage onNavigate={(k) => setActive(k as Tab)} />}
      {active === 'properties' && <PropertiesPage />}
      {active === 'documents' && <DocumentsPage />}
      {active === 'requests' && <RequestsAccordion onNavigate={(k) => setActive(k as Tab)} />}
      {active === 'reports' && <ReportsPage />}
      {active === 'invoices' && <InvoicesPage />}
      {active === 'appeals' && <AppealsPage />}
      {active === 'comments' && <CommentsPage />}
      {active === 'notifications' && <NotificationsPage notifications={allNotifs} loading={notifs.isLoading} onNavigate={(k) => setActive(k as Tab)} />}
      {active === 'signature' && <SignaturePage />}
    </PortalLayout>
  );
}

// ── Notifications (D3) ───────────────────────────────────────────────────────
// The client's notification feed: documents shared, appeal-stage advances, tax-bill
// transmittals, property-request updates, and firm replies. Each row deep-links to
// the tab that owns it. Marked read on entry (the parent effect).
function NotificationsPage({ notifications, loading, onNavigate }: {
  notifications: NotifRow[]; loading: boolean; onNavigate: (tab: string) => void;
}) {
  const tabFor = (trigger: string): Tab | null => {
    if (trigger === 'document_shared' || trigger === 'transmittal_shared') return 'documents';
    if (trigger === 'appeal_stage_advanced') return 'appeals';
    if (trigger === 'property_request_updated' || trigger === 'annual_info_request' || trigger === 'document_request') return 'requests';
    if (trigger === 'firm_comment_added') return 'comments';
    return null;
  };

  if (loading) return <p style={{ color: 'var(--color-ink-3)' }}>Loading notifications…</p>;
  if (notifications.length === 0) {
    return (
      <div style={{
        padding: 'var(--sp-6)', textAlign: 'center', color: 'var(--color-ink-3)',
        background: 'var(--color-card)', border: '1px dashed var(--color-border)', borderRadius: 'var(--r-lg)',
      }}>
        You&rsquo;re all caught up — no notifications yet.
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
      {notifications.map((n) => {
        const tab = tabFor(n.trigger_type);
        const unread = isUnread(n);
        return (
          <div
            key={n.id}
            onClick={() => tab && onNavigate(tab)}
            role={tab ? 'button' : undefined}
            tabIndex={tab ? 0 : undefined}
            onKeyDown={(e) => { if (tab && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); onNavigate(tab); } }}
            style={{
              display: 'flex', gap: 'var(--sp-3)', alignItems: 'flex-start',
              background: 'var(--color-card)', border: '1px solid var(--color-border)',
              borderLeft: unread ? '3px solid var(--brand-primary)' : '1px solid var(--color-border)',
              borderRadius: 'var(--r-lg)', padding: 'var(--sp-3) var(--sp-4)',
              cursor: tab ? 'pointer' : 'default',
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)' }}>
                {unread && <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--brand-primary)', flexShrink: 0 }} />}
                <span style={{ fontWeight: 'var(--fw-semibold)', color: 'var(--color-ink)' }}>{n.title}</span>
              </div>
              {n.body && <div style={{ marginTop: 2, fontSize: 'var(--fs-sm)', color: 'var(--color-ink-3)', overflowWrap: 'anywhere' }}>{n.body}</div>}
              <div style={{ marginTop: 4, fontSize: 'var(--fs-xs)', color: 'var(--color-muted)' }}>{dt(n.created_at)}{tab ? ' · View' : ''}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Overview (portal home, #39) ──────────────────────────────────────────────
// The client's at-a-glance landing: a few headline cards (each navigable to the
// tab it summarizes) + a compact "recently shared" list. Every count comes from
// the summary endpoint; recent items reuse the documents query. No raw activity
// log — the client sees their portfolio + what needs attention, not an event feed.
function SummaryPage({ onNavigate }: { onNavigate: (tab: string) => void }) {
  const q = useQuery({ queryKey: ['cp', 'summary'], queryFn: ({ signal }) => api.get<any>('/api/client-portal/summary', signal) });
  const docs = useQuery({ queryKey: ['cp', 'documents'], queryFn: ({ signal }) => api.get<DocRow[]>('/api/client-portal/documents', signal) });
  const recentDocs = (docs.data ?? []).slice(0, 5);

  return (
    <div>
      <SectionTitle>Overview</SectionTitle>
      <QueryState isLoading={q.isLoading} error={q.error} isEmpty={!q.data}>
        {q.data && (
          <>
            <div style={{ marginBottom: 16, fontSize: 15 }}>
              <strong>{q.data.client?.name}</strong>{' '}
              <Badge variant="info">{humanizeToken(q.data.client?.status || '')}</Badge>
            </div>
            <StatCardRow>
              <OverviewCard label="Properties" value={q.data.counts?.properties ?? 0} onClick={() => onNavigate('properties')} />
              <OverviewCard label="Appeals in progress" value={q.data.counts?.open_appeals ?? 0} accent="info" onClick={() => onNavigate('appeals')} />
              <OverviewCard label="Open requests" value={q.data.counts?.open_requests ?? 0} accent={(q.data.counts?.open_requests ?? 0) > 0 ? 'amber' : undefined} onClick={() => onNavigate('requests')} />
              <OverviewCard label="Invoices due" value={q.data.counts?.invoices_due ?? 0} accent={(q.data.counts?.invoices_due ?? 0) > 0 ? 'amber' : undefined} onClick={() => onNavigate('invoices')} />
            </StatCardRow>

            {Number(q.data.outstanding_balance) > 0 && (
              <div style={{ marginTop: 16, padding: '14px 16px', borderRadius: 10, border: '1px solid var(--color-border, #e2e2df)', background: 'var(--color-surface-raised, #fff)', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <div style={{ marginRight: 'auto' }}>
                  <div style={{ fontSize: 13, color: 'var(--color-muted, #666)' }}>Outstanding balance</div>
                  <div style={{ fontSize: 22, fontWeight: 700 }}>{money(q.data.outstanding_balance)}</div>
                </div>
                <Button size="sm" onClick={() => onNavigate('invoices')}>Review invoices</Button>
              </div>
            )}

            <div style={{ marginTop: 24 }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                <div style={{ fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--color-muted, #666)', marginRight: 'auto' }}>
                  Recently shared
                </div>
                <button
                  type="button"
                  onClick={() => onNavigate('documents')}
                  style={{ appearance: 'none', background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--brand-primary, #14532d)', fontSize: 13, fontWeight: 600 }}
                >View all</button>
              </div>
              <QueryState isLoading={docs.isLoading} error={docs.error} isEmpty={recentDocs.length === 0} emptyLabel="No documents shared with you yet.">
                <ul style={{ listStyle: 'none', padding: 0, margin: 0, border: '1px solid var(--color-border, #e2e2df)', borderRadius: 10, background: 'var(--color-surface-raised, #fff)' }}>
                  {recentDocs.map((d, i) => (
                    <li key={d.id}>
                      <button
                        type="button"
                        onClick={() => onNavigate('documents')}
                        style={{ appearance: 'none', background: 'none', border: 'none', width: '100%', textAlign: 'left', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', borderTop: i ? '1px solid var(--color-border-subtle, #f0f0ee)' : 'none' }}
                      >
                        <span style={{ fontSize: 14, fontWeight: 500, marginRight: 'auto', color: 'var(--color-ink, #1a1a1a)' }}>{d.name || d.filename}</span>
                        <Badge variant="neutral">{formatTagLabel(d.document_type) || d.category}</Badge>
                        <span style={{ fontSize: 12, color: 'var(--color-muted, #666)' }}>{dt(d.created_at)}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </QueryState>
            </div>
          </>
        )}
      </QueryState>
    </div>
  );
}

/** A StatCard that navigates on click (Overview headline cards). */
function OverviewCard({ label, value, accent, onClick }: { label: string; value: number; accent?: 'info' | 'amber'; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={`${label}: ${value}`}
      style={{ appearance: 'none', background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left', display: 'block' }}
    >
      <StatCard label={label} value={value} accent={accent} />
    </button>
  );
}

function PropertiesPage() {
  const q = useQuery({ queryKey: ['cp', 'properties'], queryFn: ({ signal }) => api.get<PortalProperty[]>('/api/client-portal/properties', signal) });
  const rows = q.data ?? [];
  // #37 view-only drill-in: click a property → its detail (+ parcels) → a parcel.
  const [property, setProperty] = useState<PortalProperty | null>(null);
  const [parcel, setParcel] = useState<PortalParcel | null>(null);
  return (
    <div>
      <SectionTitle>Properties</SectionTitle>
      <QueryState isLoading={q.isLoading} error={q.error} isEmpty={rows.length === 0} emptyLabel="No properties yet.">
        <PortalTable
          rows={rows}
          getKey={(r) => r.id}
          onRowClick={(r) => setProperty(r)}
          columns={[
            { header: 'Name', cell: (r) => r.name || '—' },
            { header: 'City', cell: (r) => r.city || '—' },
            { header: 'State', cell: (r) => r.state || '—' },
            { header: 'Type', cell: (r) => (r.re_pp === 'personal_property' ? 'Personal property' : 'Real estate') },
            { header: 'Status', cell: (r) => <Badge variant="neutral">{humanizeToken(r.status || '')}</Badge> },
          ]}
        />
      </QueryState>

      <PortalPropertyRequests />

      {property && (
        <PropertyDetailModal
          property={property}
          onClose={() => setProperty(null)}
          onOpenParcel={(p) => setParcel(p)}
        />
      )}
      {parcel && <ParcelDetailModal parcel={parcel} onClose={() => setParcel(null)} />}
    </div>
  );
}

// ── §2 Property setup requests (client side) ─────────────────────────────────
// Ask the firm to set up a new property; the request shows here with its status
// (Pending review / Needs your input / Added / Cancelled) and any note from the
// firm. Editable while open; cancellable with a reason.
interface PortalRequest {
  id: string; property_name: string | null; address_line1: string | null; city: string | null;
  state: string | null; postal_code: string | null; account_number: string | null;
  status: 'pending' | 'edits_requested' | 'added' | 'cancelled'; last_note: string | null; created_at: string;
}
const REQ_STATUS: Record<PortalRequest['status'], { label: string; variant: 'warning' | 'info' | 'success' | 'neutral' }> = {
  pending: { label: 'Pending review', variant: 'warning' },
  edits_requested: { label: 'Needs your input', variant: 'info' },
  added: { label: 'Added', variant: 'success' },
  cancelled: { label: 'Cancelled', variant: 'neutral' },
};
const emptyReq = { property_name: '', address_line1: '', city: '', state: '', postal_code: '', account_number: '' };

function PortalPropertyRequests() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const onErr = (e: unknown) => toast({ variant: 'error', message: (e as Error)?.message || 'Something went wrong. Please try again.' });
  const q = useQuery({ queryKey: ['cp', 'property-requests'], queryFn: ({ signal }) => api.get<PortalRequest[]>('/api/client-portal/property-requests', signal) });
  const rows = q.data ?? [];
  const [form, setForm] = useState<null | { id?: string; values: typeof emptyReq }>(null);
  const [cancelId, setCancelId] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const invalidate = () => qc.invalidateQueries({ queryKey: ['cp', 'property-requests'] });

  const save = useMutation({
    mutationFn: (f: { id?: string; values: typeof emptyReq }) =>
      f.id ? api.put(`/api/client-portal/property-requests/${f.id}`, f.values) : api.post('/api/client-portal/property-requests', f.values),
    onSuccess: () => { setForm(null); invalidate(); },
    onError: onErr,
  });
  const cancel = useMutation({
    mutationFn: ({ id, note: n }: { id: string; note: string }) => api.post(`/api/client-portal/property-requests/${id}/cancel`, { note: n }),
    onSuccess: () => { setCancelId(null); setNote(''); invalidate(); },
    onError: onErr,
  });

  const set = (k: keyof typeof emptyReq, v: string) => setForm((f) => (f ? { ...f, values: { ...f.values, [k]: v } } : f));
  const canSave = form && (form.values.property_name.trim() || form.values.address_line1.trim() || form.values.account_number.trim());

  return (
    <div style={{ marginTop: 32 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <SectionTitle>Property setup requests</SectionTitle>
        <Button variant="primary" onClick={() => setForm({ values: { ...emptyReq } })}>Request a property</Button>
      </div>
      <QueryState isLoading={q.isLoading} error={q.error} isEmpty={rows.length === 0} emptyLabel="No requests yet — use “Request a property” to ask us to set one up.">
        <PortalTable
          rows={rows}
          getKey={(r) => r.id}
          columns={[
            { header: 'Property', cell: (r) => r.property_name || r.address_line1 || r.account_number || '—' },
            { header: 'Status', cell: (r) => <Badge variant={REQ_STATUS[r.status].variant}>{REQ_STATUS[r.status].label}</Badge> },
            { header: 'Note from us', cell: (r) => r.last_note || '—' },
            {
              header: '', cell: (r) => (r.status === 'added' || r.status === 'cancelled') ? null : (
                <span style={{ display: 'inline-flex', gap: 8 }}>
                  <button type="button" onClick={() => setForm({ id: r.id, values: { property_name: r.property_name || '', address_line1: r.address_line1 || '', city: r.city || '', state: r.state || '', postal_code: r.postal_code || '', account_number: r.account_number || '' } })}
                    style={{ border: 'none', background: 'none', color: 'var(--brand-primary)', cursor: 'pointer', font: 'inherit', fontWeight: 600 }}>Edit</button>
                  <button type="button" onClick={() => { setCancelId(r.id); setNote(''); }}
                    style={{ border: 'none', background: 'none', color: 'var(--color-status-red)', cursor: 'pointer', font: 'inherit', fontWeight: 600 }}>Cancel</button>
                </span>
              ),
            },
          ]}
        />
      </QueryState>

      {form && (
        <Modal open onClose={() => setForm(null)} title={form.id ? 'Edit request' : 'Request a property setup'} size="lg"
          footer={<>
            <Button variant="secondary" onClick={() => setForm(null)} disabled={save.isPending}>Cancel</Button>
            <Button variant="primary" loading={save.isPending} disabled={!canSave} onClick={() => form && save.mutate(form)}>{form.id ? 'Save' : 'Submit request'}</Button>
          </>}>
          <p style={{ margin: '0 0 12px', fontSize: 14, color: 'var(--color-ink-3)' }}>Give at least a name, an address, or an account number — you can send the documents after.</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 160px), 1fr))', gap: 12 }}>
            <ReqField label="Property name"><Input value={form.values.property_name} onChange={(e) => set('property_name', e.target.value)} autoFocus /></ReqField>
            <ReqField label="Account number"><Input value={form.values.account_number} onChange={(e) => set('account_number', e.target.value)} /></ReqField>
            <ReqField label="Address"><Input value={form.values.address_line1} onChange={(e) => set('address_line1', e.target.value)} /></ReqField>
            <ReqField label="City"><Input value={form.values.city} onChange={(e) => set('city', e.target.value)} /></ReqField>
            {/* 7/28 C7: State is a dropdown; ZIP is numbers-only, 5 digits max (universal field rules). */}
            <ReqField label="State"><Select value={form.values.state} onChange={(e) => set('state', e.target.value)} placeholder="Select…" options={US_STATES.map((s) => ({ value: s, label: s }))} /></ReqField>
            <ReqField label="ZIP"><Input value={form.values.postal_code} onChange={(e) => set('postal_code', e.target.value.replace(/\D/g, '').slice(0, 5))} inputMode="numeric" maxLength={5} placeholder="12345" /></ReqField>
          </div>
        </Modal>
      )}

      {cancelId && (
        <Modal open onClose={() => setCancelId(null)} title="Cancel this request" size="sm"
          footer={<>
            <Button variant="secondary" onClick={() => setCancelId(null)} disabled={cancel.isPending}>Back</Button>
            <Button variant="danger" loading={cancel.isPending} disabled={!note.trim()} onClick={() => cancel.mutate({ id: cancelId, note: note.trim() })}>Cancel request</Button>
          </>}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: 12, color: 'var(--color-ink-3)' }}>Let us know why (required).</span>
            <textarea value={note} autoFocus onChange={(e) => setNote(e.target.value)} rows={3}
              style={{ fontFamily: 'inherit', fontSize: 15, padding: '8px 12px', width: '100%', resize: 'vertical', border: '1px solid var(--color-border)', borderRadius: 8, background: 'var(--color-card)', color: 'var(--color-ink)' }} />
          </label>
        </Modal>
      )}
    </div>
  );
}

function ReqField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-ink-2)' }}>{label}</span>
      {children}
    </label>
  );
}

// ── Documents view (portal V1) ───────────────────────────────────────────────

interface DocRow {
  id: string; name: string; filename: string; document_type: string;
  content_type: string | null;
  year: number | null; created_at: string; category: string;
  property_id: string | null; property_name: string | null; state: string | null;
}

function DocumentsPage() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const q = useQuery({ queryKey: ['cp', 'documents'], queryFn: ({ signal }) => api.get<DocRow[]>('/api/client-portal/documents', signal) });
  // The portal groups documents into a FIXED set of client-facing category buckets
  // (E1/E2 — there is no separate "portal document types" list any more). The firm
  // types every document from the single master list at review; the backend
  // categorizeDocType maps that type into one of these buckets for the filter chips.
  // "Other" is always offered as the catch-all.
  const categories = withOtherCategory([...DEFAULT_PORTAL_DOC_CATEGORIES]);
  const [category, setCategory] = useState('All');
  const [search, setSearch] = useState('');
  const [viewing, setViewing] = useState<DocRow | null>(null);
  const rows = q.data ?? [];

  // Open the inline viewer AND log the view (PRD 4.5): the portal's existing
  // POST /viewed write is what surfaces "client viewed" to staff. Fire it once on
  // open (downloaded:false — this is a VIEW, not a download); best-effort.
  const openViewer = (r: DocRow) => {
    setViewing(r);
    api.post(`/api/client-portal/documents/${r.id}/viewed`, { downloaded: false })
      .then(() => qc.invalidateQueries({ queryKey: ['cp', 'banner'] }))
      .catch(() => {});
  };

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (category !== 'All' && r.category !== category) return false;
      if (!s) return true;
      return (r.name || '').toLowerCase().includes(s)
        || (r.filename || '').toLowerCase().includes(s)
        || (r.property_name || '').toLowerCase().includes(s);
    });
  }, [rows, category, search]);

  // Group STATE → PROPERTY → docs (years shown per row, sorted desc).
  const grouped = useMemo(() => {
    const states = new Map<string, Map<string, DocRow[]>>();
    for (const r of filtered) {
      const st = r.state || 'Other';
      const prop = r.property_name || 'Account documents';
      if (!states.has(st)) states.set(st, new Map());
      const props = states.get(st)!;
      if (!props.has(prop)) props.set(prop, []);
      props.get(prop)!.push(r);
    }
    for (const props of states.values()) {
      for (const list of props.values()) {
        list.sort((a, b) => (b.year ?? 0) - (a.year ?? 0) || (b.created_at > a.created_at ? 1 : -1));
      }
    }
    return states;
  }, [filtered]);

  return (
    <div>
      <SectionTitle>Documents</SectionTitle>
      <UploadPanel onUploaded={() => qc.invalidateQueries({ queryKey: ['cp', 'documents'] })} />

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', margin: '4px 0 16px' }}>
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {filterCategories(categories).map((c) => {
            const on = c === category;
            return (
              <button
                key={c}
                onClick={() => setCategory(c)}
                style={{
                  appearance: 'none', cursor: 'pointer', padding: '6px 12px', fontSize: 13, borderRadius: 999,
                  border: '1px solid var(--color-border, #e2e2df)',
                  background: on ? 'var(--brand-primary, #14532d)' : 'var(--color-surface-raised, #fff)',
                  color: on ? 'var(--brand-on-primary, #fff)' : 'var(--color-ink, #1a1a1a)',
                  fontWeight: on ? 600 : 400,
                }}
              >{c}</button>
            );
          })}
        </div>
        <div style={{ marginLeft: 'auto', minWidth: 220 }}>
          <Input placeholder="Search property or document…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
      </div>

      <QueryState isLoading={q.isLoading} error={q.error} isEmpty={filtered.length === 0} emptyLabel="No documents match.">
        {[...grouped.entries()].map(([st, props]) => (
          <div key={st} style={{ marginBottom: 24 }}>
            <div style={{ fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--color-muted, #666)', margin: '0 0 8px' }}>{st}</div>
            {[...props.entries()].map(([prop, list]) => (
              <div key={prop} style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 14, fontWeight: 600, margin: '0 0 6px' }}>{prop}</div>
                <PortalTable
                  rows={list}
                  getKey={(r) => r.id}
                  columns={[
                    {
                      header: 'Document',
                      cell: (r) => (
                        <button
                          type="button"
                          onClick={() => openViewer(r)}
                          style={{
                            appearance: 'none', background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                            color: 'var(--brand-primary, #14532d)', fontWeight: 600, textAlign: 'left',
                            textDecoration: 'underline', font: 'inherit',
                          }}
                        >{r.name || r.filename}</button>
                      ),
                    },
                    { header: 'Type', cell: (r) => <Badge variant="neutral">{formatTagLabel(r.document_type) || r.category}</Badge> },
                    { header: 'Year', cell: (r) => r.year ?? '—' },
                    { header: 'Shared', cell: (r) => dt(r.created_at) },
                    {
                      header: '', align: 'right',
                      cell: (r) => (
                        <span style={{ display: 'inline-flex', gap: 12 }}>
                          <button
                            type="button"
                            onClick={() => openViewer(r)}
                            style={{ appearance: 'none', background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--brand-primary, #14532d)', font: 'inherit' }}
                          >View</button>
                          <button
                            type="button"
                            style={{ appearance: 'none', background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--brand-primary, #14532d)', font: 'inherit' }}
                            onClick={() => {
                              downloadDocument(r.id, (m) => toast({ variant: 'error', message: m }), `/api/client-portal/documents/${r.id}/download`);
                              api.post(`/api/client-portal/documents/${r.id}/viewed`, { downloaded: true })
                                .then(() => qc.invalidateQueries({ queryKey: ['cp', 'banner'] }))
                                .catch(() => {});
                            }}
                          >Download</button>
                        </span>
                      ),
                    },
                  ]}
                />
              </div>
            ))}
          </div>
        ))}
      </QueryState>

      {viewing && (
        <DocumentViewer
          id={viewing.id}
          name={viewing.name}
          filename={viewing.filename}
          content_type={viewing.content_type}
          downloadBase={`/api/client-portal/documents/${viewing.id}/download`}
          onClose={() => setViewing(null)}
        />
      )}
    </div>
  );
}

/**
 * "Send us a document" — a two-step choose→confirm upload. Picking a file does
 * NOT upload; it stages the file locally. The client picks a category from the
 * org's configured portal document types (the same list the Documents section
 * filters by) and clicks "Upload" to confirm. The chosen label is sent as the
 * document's `document_type` directly, so the server files it back under that
 * same label — one shared vocabulary between the portal and the firm.
 */
function UploadPanel({ onUploaded }: { onUploaded: () => void }) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploaded, setUploaded] = useState<string | null>(null);

  const clearInput = () => { if (fileRef.current) fileRef.current.value = ''; };

  const mut = useMutation({
    mutationFn: async (file: File) => {
      const file_data = await fileToBase64(file);
      // E3 — the client no longer classifies (no dropdown). Everything arrives
      // uncategorized ('Other') and hits the firm's document review queue, where
      // staff assign the correct type. Classifying here just produced mis-tagged docs.
      return api.post('/api/client-portal/documents', {
        name: file.name, filename: file.name,
        content_type: file.type || 'application/octet-stream',
        file_data, document_type: 'Other',
      });
    },
    onSuccess: (_data, file) => {
      setUploaded(file.name);
      setSelectedFile(null);
      clearInput();
      onUploaded();
    },
  });

  const removeFile = () => { setSelectedFile(null); clearInput(); };

  return (
    <div
      className="upload-panel"
      style={{
        border: '1px dashed var(--color-border, #cfe3d6)', borderRadius: 10, padding: '14px 16px',
        background: 'var(--color-surface-raised, #fff)', margin: '0 0 16px',
        display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center',
      }}
    >
      <div style={{ fontSize: 14 }}>
        <strong>Send us a document</strong>
        <div style={{ fontSize: 12, color: 'var(--color-muted, #666)' }}>Choose a file and upload — we’ll review and file it for you.</div>
      </div>
      <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <input
          ref={fileRef}
          type="file"
          style={{ display: 'none' }}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) { setSelectedFile(f); setUploaded(null); }
          }}
        />
        <Button size="sm" variant="secondary" onClick={() => fileRef.current?.click()}>
          {selectedFile ? 'Change file' : 'Choose file'}
        </Button>
        <Button size="sm" disabled={!selectedFile} loading={mut.isPending} onClick={() => { if (selectedFile) mut.mutate(selectedFile); }}>
          Upload
        </Button>
      </div>

      {selectedFile && !mut.isPending && (
        <div style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, fontSize: 12, color: 'var(--color-ink, #1a1a1a)' }}>
          <span>Ready to upload <strong>{selectedFile.name}</strong>.</span>
          <button
            type="button"
            onClick={removeFile}
            style={{ appearance: 'none', background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--brand-primary, #14532d)', font: 'inherit', textDecoration: 'underline' }}
          >Remove</button>
        </div>
      )}
      {uploaded && !selectedFile && !mut.isPending && !mut.isError && (
        <div style={{ width: '100%', fontSize: 12, color: 'var(--color-status-green, #14532d)' }}>Uploaded {uploaded} — we’ll review it.</div>
      )}
      {mut.isError && (
        <div style={{ width: '100%', fontSize: 12, color: 'var(--color-status-red, #b00)' }}>Upload failed. Please try again.</div>
      )}
    </div>
  );
}

// ── Reports view (portal V1) ─────────────────────────────────────────────────

interface ReportMeta { slug: string; label: string; description: string; columns: { key: string; label: string }[] }
interface ReportResult { slug: string; label: string; columns: { key: string; label: string }[]; rows: any[]; meta: { row_count: number } }

const MONEY_LABEL = /\(\$\)|amount|value|billed|tax\b/i;
function fmtCell(col: { key: string; label: string }, row: any) {
  const v = row[col.key];
  if (v === null || v === undefined) return '—';
  if (typeof v === 'number' && MONEY_LABEL.test(col.label)) return money(v);
  return String(v);
}

function ReportsPage() {
  const q = useQuery({ queryKey: ['cp', 'reports'], queryFn: ({ signal }) => api.get<{ reports: ReportMeta[] }>('/api/client-portal/reports', signal) });
  const [active, setActive] = useState<string | null>(null);
  const reports = q.data?.reports ?? [];

  const run = useQuery({
    queryKey: ['cp', 'report', active],
    enabled: !!active,
    queryFn: ({ signal }) => api.get<ReportResult>(`/api/client-portal/reports/${active}`, signal),
  });

  return (
    <div>
      <SectionTitle>Reports</SectionTitle>
      <QueryState isLoading={q.isLoading} error={q.error} isEmpty={reports.length === 0} emptyLabel="No reports available.">
        <div style={{ display: 'grid', gap: 10, gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 260px), 1fr))', marginBottom: 20 }}>
          {reports.map((r) => (
            <div key={r.slug} style={{ border: '1px solid var(--color-border, #e2e2df)', borderRadius: 10, padding: 14, background: 'var(--color-surface-raised, #fff)' }}>
              <div style={{ fontWeight: 600, fontSize: 15 }}>{r.label}</div>
              <div style={{ fontSize: 12, color: 'var(--color-muted, #666)', margin: '4px 0 12px', minHeight: 32 }}>{r.description}</div>
              <div style={{ display: 'flex', gap: 8 }}>
                <Button size="sm" onClick={() => setActive(r.slug)} loading={run.isFetching && active === r.slug}>Run</Button>
                <a
                  href={`/api/client-portal/reports/${r.slug}?format=csv`}
                  style={{ fontSize: 13, color: 'var(--brand-primary, #14532d)', alignSelf: 'center' }}
                >Download CSV</a>
                <a
                  href={`/api/client-portal/reports/${r.slug}?format=xlsx`}
                  style={{ fontSize: 13, color: 'var(--brand-primary, #14532d)', alignSelf: 'center' }}
                >Export XLSX</a>
                <a
                  href={`/api/client-portal/reports/${r.slug}?format=pdf`}
                  style={{ fontSize: 13, color: 'var(--brand-primary, #14532d)', alignSelf: 'center' }}
                >Download PDF</a>
              </div>
            </div>
          ))}
        </div>

        {active && (
          <QueryState isLoading={run.isLoading} error={run.error} isEmpty={(run.data?.rows.length ?? 0) === 0} emptyLabel="No rows for your account.">
            {run.data && (
              <div>
                <div style={{ fontSize: 15, fontWeight: 600, margin: '0 0 8px' }}>
                  {run.data.label}{' '}
                  <span style={{ fontSize: 12, color: 'var(--color-muted, #666)' }}>({run.data.meta.row_count} rows)</span>
                </div>
                <PortalTable
                  rows={run.data.rows}
                  getKey={(_r, i) => String(i)}
                  columns={run.data.columns.map((c) => ({ header: c.label, cell: (row: any) => fmtCell(c, row) }))}
                />
              </div>
            )}
          </QueryState>
        )}
      </QueryState>
    </div>
  );
}

function InvoicesPage() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['cp', 'invoices'], queryFn: ({ signal }) => api.get<PortalInvoice[]>('/api/client-portal/invoices', signal) });
  const rows = q.data ?? [];
  // The invoice whose notes thread is open (null = closed).
  const [notesInvoice, setNotesInvoice] = useState<{ id: string; invoice_number: string } | null>(null);
  // #37: click an invoice row → its read-only detail (which itself carries Pay).
  const [detailInvoice, setDetailInvoice] = useState<PortalInvoice | null>(null);

  // An invoice is payable from the portal when it carries an outstanding balance
  // AND its status is one the server will actually collect against. The backend
  // authority is PAYABLE_INVOICE_STATUSES = {approved, sent, partially_paid, paid};
  // 'paid' has no balance, so the affordance set is the three open statuses. A
  // freshly generated invoice ('pending'/'prepared') carries a balance but is NOT
  // yet payable — offering Pay there 400s and, in 'Pay all', dead-ends the batch.
  const isPayable = (r: PortalInvoice) =>
    Number(r.balance) > 0 && ['approved', 'sent', 'partially_paid'].includes(r.status);
  const payable: PayableInvoice[] = rows
    .filter(isPayable)
    .map((r) => ({ id: r.id, invoice_number: r.invoice_number, amount: Number(r.amount), balance: Number(r.balance), status: r.status }));

  // Refetch invoices + summary after a payment settles so balances update live.
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['cp', 'invoices'] });
    qc.invalidateQueries({ queryKey: ['cp', 'summary'] });
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <SectionTitle>Invoices</SectionTitle>
        {payable.length > 1 && (
          <div style={{ marginLeft: 'auto' }}>
            <PayAllButton invoices={payable} onPaid={refresh} />
          </div>
        )}
      </div>
      <QueryState isLoading={q.isLoading} error={q.error} isEmpty={rows.length === 0} emptyLabel="No invoices yet.">
        <PortalTable
          rows={rows}
          getKey={(r) => r.id}
          onRowClick={(r) => setDetailInvoice(r)}
          columns={[
            { header: 'Invoice #', cell: (r) => r.invoice_number || r.id.slice(0, 8) },
            { header: 'Status', cell: (r) => <Badge variant="info">{humanizeToken(r.status || '')}</Badge> },
            { header: 'Amount', align: 'right', cell: (r) => money(r.amount) },
            { header: 'Balance', align: 'right', cell: (r) => money(r.balance) },
            { header: 'Due', cell: (r) => dt(r.due_date) },
            {
              header: '', align: 'right',
              // Actions stop row-click propagation so Notes/Pay don't also open detail.
              cell: (r) => (
                <div
                  style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center' }}
                  onClick={(e) => e.stopPropagation()}
                  role="presentation"
                >
                  <Button size="sm" variant="secondary" onClick={() => setNotesInvoice({ id: r.id, invoice_number: r.invoice_number || r.id.slice(0, 8) })}>
                    Notes
                  </Button>
                  {isPayable(r) && (
                    <PayInvoiceButton
                      invoice={{ id: r.id, invoice_number: r.invoice_number, amount: Number(r.amount), balance: Number(r.balance), status: r.status }}
                      onPaid={refresh}
                    />
                  )}
                </div>
              ),
            },
          ]}
        />
      </QueryState>
      {detailInvoice && (
        <InvoiceDetailModal invoice={detailInvoice} onClose={() => setDetailInvoice(null)} onPaid={refresh} />
      )}
      {notesInvoice && (
        <InvoiceNotesModal invoice={notesInvoice} onClose={() => setNotesInvoice(null)} />
      )}
    </div>
  );
}

// ── Invoice notes (firm ⇆ client) ────────────────────────────────────────────
// Read the firm's notes on this invoice and let the client reply, over
// /api/client-portal/invoices/:id/notes (server-scoped to the client's own
// invoices). Same threaded shape as the account Comments thread, so it reuses
// threadComments + CommentItem + CommentComposer.
function InvoiceNotesModal({ invoice, onClose }: { invoice: { id: string; invoice_number: string }; onClose: () => void }) {
  const qc = useQueryClient();
  const notesKey = ['cp', 'invoice-notes', invoice.id];
  const q = useQuery({ queryKey: notesKey, queryFn: ({ signal }) => api.get<CommentRow[]>(`/api/client-portal/invoices/${invoice.id}/notes`, signal) });
  const [text, setText] = useState('');
  const [replyingId, setReplyingId] = useState<string | null>(null);
  const [replyText, setReplyText] = useState('');
  const rows = q.data ?? [];
  const threads = useMemo(() => threadComments(rows), [rows]);

  const mut = useMutation({
    mutationFn: (body: { content: string; parent_id?: string }) => api.post(`/api/client-portal/invoices/${invoice.id}/notes`, body),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: notesKey });
      if (vars.parent_id) { setReplyText(''); setReplyingId(null); } else { setText(''); }
    },
  });

  const submitReply = (parentId: string) => {
    if (!replyText.trim()) return;
    mut.mutate({ content: replyText.trim(), parent_id: parentId });
  };

  return (
    <Modal open onClose={onClose} title={`Invoice #${invoice.invoice_number} — Notes`} size="lg">
      <div style={{ fontSize: 13, color: 'var(--color-muted, #666)', margin: '0 0 12px' }}>
        Notes your team has left on this invoice. Reply here and they’ll be notified.
      </div>

      <div style={{ marginBottom: 20 }}>
        <CommentComposer
          value={text}
          onChange={setText}
          onSubmit={() => mut.mutate({ content: text.trim() })}
          pending={mut.isPending && !replyingId}
          placeholder="Add a note or question about this invoice…"
          submitLabel="Post note"
        />
        {mut.isError && <div style={{ fontSize: 12, color: 'var(--color-status-red, #b00)' }}>Couldn’t post. Please try again.</div>}
      </div>

      <QueryState isLoading={q.isLoading} error={q.error} isEmpty={rows.length === 0} emptyLabel="No notes on this invoice yet.">
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {threads.map((node) => (
            <CommentItem
              key={node.id}
              node={node}
              onReply={(id) => { setReplyingId(id); setReplyText(''); }}
              replyingId={replyingId}
              setReplyingId={setReplyingId}
              replyText={replyText}
              setReplyText={setReplyText}
              submitReply={submitReply}
              replyPending={mut.isPending && !!replyingId}
            />
          ))}
        </ul>
      </QueryState>
    </Modal>
  );
}

function AppealsPage() {
  const q = useQuery({ queryKey: ['cp', 'appeals'], queryFn: ({ signal }) => api.get<PortalAppeal[]>('/api/client-portal/appeals', signal) });
  const rows = q.data ?? [];
  const [appeal, setAppeal] = useState<PortalAppeal | null>(null); // #37 view-only detail
  return (
    <div>
      <SectionTitle>Appeals</SectionTitle>
      <QueryState isLoading={q.isLoading} error={q.error} isEmpty={rows.length === 0} emptyLabel="No appeals yet.">
        <PortalTable
          rows={rows}
          getKey={(r) => r.id}
          onRowClick={(r) => setAppeal(r)}
          columns={[
            { header: 'Parcel', cell: (r) => r.parcel_name || r.account_number },
            { header: 'Year', cell: (r) => r.tax_year },
            { header: 'Stage', cell: (r) => <Badge variant="info">{String(r.lifecycle_stage).replace(/_/g, ' ')}</Badge> },
            { header: 'Hearing', cell: (r) => dt(r.hearing_date) },
            { header: 'Outcome', cell: (r) => r.outcome || '—' },
          ]}
        />
      </QueryState>
      {appeal && <AppealDetailModal appeal={appeal} onClose={() => setAppeal(null)} />}
    </div>
  );
}

// ── Comments view (threaded, 09.4) ──────────────────────────────────────────
// Reads the flat comment list (each row carries parent_id) and nests replies one
// level under their parent. Posting a top-level comment OR a reply hits the SAME
// /api/client-portal/comments endpoint (reply adds {parent_id}); a firm reply
// emails the client and vice-versa, so the conversation stays in sync by email.

interface CommentRow { id: string; content: string; created_at: string; parent_id: string | null; is_self: boolean; author_type: string; author_name: string }
interface CommentNode extends CommentRow { replies: CommentNode[] }

// Group flat rows into top-level threads with their (chronological) replies. A
// reply whose parent is missing (edge case) is promoted to top-level so it can't
// vanish from the conversation.
function threadComments(rows: CommentRow[]): CommentNode[] {
  const byId = new Map<string, CommentNode>();
  rows.forEach((r) => byId.set(r.id, { ...r, replies: [] }));
  const roots: CommentNode[] = [];
  rows.forEach((r) => {
    const node = byId.get(r.id)!;
    const parent = r.parent_id ? byId.get(r.parent_id) : undefined;
    if (parent) parent.replies.push(node);
    else roots.push(node);
  });
  return roots;
}

function CommentComposer({ value, onChange, onSubmit, pending, placeholder, submitLabel }: {
  value: string; onChange: (v: string) => void; onSubmit: () => void; pending: boolean; placeholder: string; submitLabel: string;
}) {
  return (
    <div>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={3}
        style={{
          width: '100%', boxSizing: 'border-box', padding: '10px 12px', fontSize: 14, borderRadius: 8,
          border: '1px solid var(--color-border, #e2e2df)', resize: 'vertical', fontFamily: 'inherit',
          background: 'var(--color-surface-raised, #fff)', color: 'var(--color-ink, #1a1a1a)',
        }}
      />
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
        <Button size="sm" disabled={!value.trim()} loading={pending} onClick={onSubmit}>{submitLabel}</Button>
      </div>
    </div>
  );
}

function CommentItem({ node, onReply, replyingId, setReplyingId, replyText, setReplyText, submitReply, replyPending }: {
  node: CommentNode;
  onReply: (id: string) => void;
  replyingId: string | null;
  setReplyingId: (id: string | null) => void;
  replyText: string;
  setReplyText: (v: string) => void;
  submitReply: (parentId: string) => void;
  replyPending: boolean;
}) {
  const isReplying = replyingId === node.id;
  return (
    <li style={{ padding: '12px 0', borderBottom: '1px solid var(--color-border-subtle, #f0f0ee)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <Badge variant={node.author_type === 'client' ? 'info' : 'neutral'}>{node.is_self ? 'You' : node.author_name}</Badge>
        <span style={{ fontSize: 12, color: 'var(--color-muted, #666)' }}>{dt(node.created_at)}</span>
      </div>
      <div style={{ fontSize: 14, whiteSpace: 'pre-wrap' }}>{node.content}</div>

      <div style={{ marginTop: 6 }}>
        <button
          type="button"
          onClick={() => (isReplying ? setReplyingId(null) : onReply(node.id))}
          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 12, color: 'var(--brand-primary, #1D4E45)', fontWeight: 500 }}
        >{isReplying ? 'Cancel' : 'Reply'}</button>
      </div>

      {isReplying && (
        <div style={{ marginTop: 8 }}>
          <CommentComposer
            value={replyText}
            onChange={setReplyText}
            onSubmit={() => submitReply(node.id)}
            pending={replyPending}
            placeholder="Write a reply…"
            submitLabel="Post reply"
          />
        </div>
      )}

      {node.replies.length > 0 && (
        <ul style={{ listStyle: 'none', padding: 0, margin: '4px 0 0', borderLeft: '2px solid var(--color-border-subtle, #f0f0ee)', paddingLeft: 12 }}>
          {node.replies.map((r) => (
            <CommentItem
              key={r.id}
              node={r}
              onReply={onReply}
              replyingId={replyingId}
              setReplyingId={setReplyingId}
              replyText={replyText}
              setReplyText={setReplyText}
              submitReply={submitReply}
              replyPending={replyPending}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

function CommentsPage() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['cp', 'comments'], queryFn: ({ signal }) => api.get<CommentRow[]>('/api/client-portal/comments', signal) });
  const [text, setText] = useState('');
  const [replyingId, setReplyingId] = useState<string | null>(null);
  const [replyText, setReplyText] = useState('');
  const rows = q.data ?? [];
  const threads = useMemo(() => threadComments(rows), [rows]);

  const mut = useMutation({
    mutationFn: (body: { content: string; parent_id?: string }) => api.post('/api/client-portal/comments', body),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['cp', 'comments'] });
      if (vars.parent_id) { setReplyText(''); setReplyingId(null); } else { setText(''); }
    },
  });

  const submitReply = (parentId: string) => {
    if (!replyText.trim()) return;
    mut.mutate({ content: replyText.trim(), parent_id: parentId });
  };

  return (
    <div>
      <SectionTitle>Comments</SectionTitle>
      <div style={{ fontSize: 13, color: 'var(--color-muted, #666)', margin: '0 0 12px' }}>
        Message your team — posting a comment emails them, and their replies email you, so nothing slips through.
      </div>

      <div style={{ marginBottom: 20 }}>
        <CommentComposer
          value={text}
          onChange={setText}
          onSubmit={() => mut.mutate({ content: text.trim() })}
          pending={mut.isPending && !replyingId}
          placeholder="Write a comment…"
          submitLabel="Post comment"
        />
        {mut.isError && <div style={{ fontSize: 12, color: 'var(--color-status-red, #b00)' }}>Couldn’t post. Please try again.</div>}
      </div>

      <QueryState isLoading={q.isLoading} error={q.error} isEmpty={rows.length === 0} emptyLabel="No comments yet — start the conversation.">
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {threads.map((node) => (
            <CommentItem
              key={node.id}
              node={node}
              onReply={(id) => { setReplyingId(id); setReplyText(''); }}
              replyingId={replyingId}
              setReplyingId={setReplyingId}
              replyText={replyText}
              setReplyText={setReplyText}
              submitReply={submitReply}
              replyPending={mut.isPending && !!replyingId}
            />
          ))}
        </ul>
      </QueryState>
    </div>
  );
}

// ── Signature (reusable e-signature, gm4-portal-signatures) ──────────────────
// The client draws + saves a signature once; it's kept on file (server-scoped to
// this portal user's own client) and reused on forms/authorizations. If one is
// already saved we render the image with a "Re-sign" affordance that reveals the
// pad again; otherwise the pad shows immediately.

interface SavedSignature {
  id: string; signature_data: string; label: string | null;
  created_at: string; updated_at: string;
}

function SignaturePage() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['cp', 'signature'],
    queryFn: ({ signal }) => api.get<{ signature: SavedSignature | null }>('/api/client-portal/signature', signal),
  });
  const [resigning, setResigning] = useState(false);
  const saved = q.data?.signature ?? null;

  const mut = useMutation({
    mutationFn: (dataUrl: string) => api.post('/api/client-portal/signature', { signature_data: dataUrl }),
    onSuccess: () => {
      setResigning(false);
      qc.invalidateQueries({ queryKey: ['cp', 'signature'] });
    },
  });

  const showPad = !saved || resigning;

  return (
    <div>
      <SectionTitle>Signature</SectionTitle>
      <div style={{ fontSize: 13, color: 'var(--color-muted, #666)', margin: '0 0 16px' }}>
        Draw your signature once and we’ll keep it on file to reuse on forms and authorizations.
      </div>

      <QueryState isLoading={q.isLoading} error={q.error} isEmpty={false}>
        {saved && !resigning && (
          <div
            style={{
              border: '1px solid var(--color-border, #e2e2df)', borderRadius: 10, padding: 16,
              background: 'var(--color-surface-raised, #fff)', maxWidth: 520,
            }}
          >
            <div style={{ fontSize: 12, color: 'var(--color-muted, #666)', marginBottom: 8 }}>Signature on file</div>
            <img
              src={saved.signature_data}
              alt="Your saved signature"
              style={{
                display: 'block', maxWidth: '100%', maxHeight: 180,
                border: '1px solid var(--color-border-subtle, #f0f0ee)', borderRadius: 8,
                background: '#fff',
              }}
            />
            <div style={{ fontSize: 12, color: 'var(--color-muted, #666)', margin: '10px 0 12px' }}>
              Saved {formatDateTime(saved.updated_at)}
            </div>
            <Button size="sm" variant="secondary" onClick={() => setResigning(true)}>Re-sign</Button>
          </div>
        )}

        {showPad && (
          <div style={{ maxWidth: 520 }}>
            <SignaturePad onSave={(dataUrl) => mut.mutate(dataUrl)} saving={mut.isPending} />
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 8 }}>
              {saved && (
                <button
                  type="button"
                  onClick={() => setResigning(false)}
                  style={{ appearance: 'none', background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--brand-primary, #14532d)', font: 'inherit', textDecoration: 'underline' }}
                >Cancel</button>
              )}
              {mut.isError && (
                <span style={{ fontSize: 12, color: 'var(--color-status-red, #b00)' }}>Couldn’t save your signature. Please try again.</span>
              )}
            </div>
          </div>
        )}
      </QueryState>
    </div>
  );
}
